import { RadioGroup } from "@headlessui/react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { RadioIndicator } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { OrSeparator } from "@/components/Layout/OrSeparator";
import { ChangeTwoFactorAuthenticationMethod } from "@/services/model/changeTwoFactorAuthenticationMethod";

export type AuthenticatorSetupMethodsForm = {
  type: keyof typeof ChangeTwoFactorAuthenticationMethod | null;
};

/**
 * Small security-level chip. The pill sits on the neutral `surface-quaternary`
 * background; only the label colour changes: `info` for the secure options,
 * `warning` for the not-recommended one.
 */
const SecurityChip = ({
  text,
  variant,
}: {
  text: string;
  variant: "info" | "warning";
}) => (
  <span
    className={clsx(
      "inline-flex items-center rounded-full bg-surface-quaternary px-2 py-1 text-xs font-semibold leading-none tracking-[0.24px]",
      variant === "info" ? "text-foreground-info" : "text-foreground-warning",
    )}
  >
    {text}
  </span>
);

type OptionProps = {
  /** When true, render only the option label + chip — no descriptions. */
  compact?: boolean;
};

export const AuthenticatorSetupFormElements = ({ compact }: OptionProps) => {
  useTranslation("translation");

  return (
    <>
      <TwoFactorAppOption compact={compact} />
      <TwoFactorSmsOption compact={compact} />
      {!compact && <OrSeparator />}
      <TwoFactorStandardOption compact={compact} />
    </>
  );
};

/** Every option below must render inside an ancestor `RadioGroup` (see the form components that own `useForm`). */
export const TwoFactorAppOption = ({ compact }: OptionProps) => {
  const { t } = useTranslation("translation");

  return (
    <div>
      <RadioGroup.Option
        value={ChangeTwoFactorAuthenticationMethod.APP}
        className="outline-none"
      >
        {({ checked }) => (
          <div className="flex items-center gap-3">
            <RadioIndicator checked={checked} />
            <span className="-mt-1 flex flex-wrap items-center gap-2 text-lg font-semibold text-foreground-primary">
              {t("2fa.setup.options.authenticatorApp.title")}
              <SecurityChip
                text={t("2fa.setup.options.very-secure")}
                variant="info"
              />
            </span>
          </div>
        )}
      </RadioGroup.Option>
      {!compact && (
        <div className="ml-9 text-base text-foreground-primary">
          <p>{t("2fa.setup.options.authenticatorApp.description.intro")}</p>
          <ol className="list-inside">
            <li className="list-decimal">
              {t("2fa.setup.options.authenticatorApp.description.download")}
            </li>
            <li className="list-decimal">
              {t("2fa.setup.options.authenticatorApp.description.clickNext")}
            </li>
          </ol>
        </div>
      )}
    </div>
  );
};

export const TwoFactorSmsOption = ({ compact }: OptionProps) => {
  const { t } = useTranslation("translation");

  return (
    <div>
      <RadioGroup.Option
        value={ChangeTwoFactorAuthenticationMethod.SMS}
        className="outline-none"
      >
        {({ checked }) => (
          <div className="flex items-center gap-3">
            <RadioIndicator checked={checked} />
            <span className="-mt-1 flex flex-wrap items-center gap-2 text-lg font-semibold text-foreground-primary">
              {t("2fa.setup.options.sms.title")}
              <SecurityChip
                text={t("2fa.setup.options.secure")}
                variant="info"
              />
            </span>
          </div>
        )}
      </RadioGroup.Option>
      {!compact && (
        <div className="ml-9 text-base text-foreground-primary">
          <p>{t("2fa.setup.options.sms.description")}</p>
        </div>
      )}
    </div>
  );
};

export const TwoFactorStandardOption = ({ compact }: OptionProps) => {
  const { t } = useTranslation("translation");

  return (
    <div>
      <RadioGroup.Option
        value={ChangeTwoFactorAuthenticationMethod.STANDARD}
        className="outline-none"
      >
        {({ checked }) => (
          <div className="flex items-center gap-3">
            <RadioIndicator checked={checked} />
            <span className="-mt-1 flex flex-wrap items-center gap-2 text-lg font-semibold text-foreground-primary">
              {t("2fa.setup.options.login.title")}
              <SecurityChip
                text={t("2fa.setup.options.not-recommended")}
                variant="warning"
              />
            </span>
          </div>
        )}
      </RadioGroup.Option>
      {!compact && (
        <p className="ml-9 text-base text-foreground-primary">
          {t("2fa.setup.options.login.description")}
        </p>
      )}
    </div>
  );
};
